import { BaseApiService } from '../base/BaseApiService';
import { ApiResponse } from '../../types/api';
import {
  DEVICE_TOKEN_HEADER,
  getStoredDeviceToken,
  setStoredAuthorizedDeviceId,
  setStoredDeviceToken,
} from '../../utils/deviceTokenStorage';
import {
  AuthorizedDeviceApiDto,
  AuthorizedDeviceViewModel,
  mapAuthorizedDeviceFromApi,
  mapAuthorizedDevicesFromApi,
} from './authorizedDeviceMappers';
import { buildAuthorizedDevicesQueryParams } from './authorizedDeviceQuery';
import { resolveAuthorizeCurrentDeviceId } from './authorizeResponseUtils';
import type {
  SetUserDeviceBypassApiRequest,
  UserDeviceBypassApiResponse,
} from './deviceUserBypassApi';

export type {
  SetUserDeviceBypassApiRequest,
  UserDeviceBypassApiResponse,
} from './deviceUserBypassApi';

export type DeviceRestrictionMode = 'off' | 'auditOnly' | 'enforce';

export interface DeviceRestrictionSettingsDto {
  mode: DeviceRestrictionMode;
}

export interface AuthorizeCurrentDeviceRequest {
  name: string;
  unit: string;
}

export interface AuthorizeCurrentDeviceApiResponse {
  deviceToken: string;
  device?: AuthorizedDeviceApiDto;
  deviceId?: string;
}

export interface RenameAuthorizedDeviceRequest {
  name: string;
}

export type AuthorizedDeviceActiveFilter = 'all' | 'active' | 'revoked';

export interface ListAuthorizedDevicesParams {
  unit?: string;
  active?: AuthorizedDeviceActiveFilter;
}

class AuthorizedDevicesService extends BaseApiService {
  constructor() {
    super();
    this.api.interceptors.request.use((config) => {
      const deviceToken = getStoredDeviceToken();
      if (deviceToken) {
        config.headers = config.headers ?? {};
        config.headers[DEVICE_TOKEN_HEADER] = deviceToken;
      }
      return config;
    });
  }

  async getRestrictionSettings(): Promise<DeviceRestrictionSettingsDto> {
    const response = await this.api.get<ApiResponse<DeviceRestrictionSettingsDto>>(
      '/device-restriction-settings'
    );
    return this.extractData(response);
  }

  async updateRestrictionSettings(
    settings: DeviceRestrictionSettingsDto
  ): Promise<DeviceRestrictionSettingsDto> {
    const response = await this.api.put<ApiResponse<DeviceRestrictionSettingsDto>>(
      '/device-restriction-settings',
      settings
    );
    return this.extractData(response);
  }

  async listDevices(params?: ListAuthorizedDevicesParams): Promise<AuthorizedDeviceViewModel[]> {
    const query = buildAuthorizedDevicesQueryParams(params);

    const response = await this.api.get<ApiResponse<AuthorizedDeviceApiDto[]>>(
      '/authorized-devices',
      { params: query }
    );
    return mapAuthorizedDevicesFromApi(this.extractData(response));
  }

  async authorizeCurrentDevice(
    payload: AuthorizeCurrentDeviceRequest
  ): Promise<AuthorizeCurrentDeviceApiResponse> {
    const response = await this.api.post<ApiResponse<AuthorizeCurrentDeviceApiResponse>>(
      '/authorized-devices/authorize-current',
      payload
    );
    const data = this.extractData(response);
    if (data.deviceToken) {
      setStoredDeviceToken(data.deviceToken);
    }
    const authorizedDeviceId = resolveAuthorizeCurrentDeviceId(data);
    if (authorizedDeviceId) {
      setStoredAuthorizedDeviceId(authorizedDeviceId);
    }
    return data;
  }

  async setUserDeviceBypass(
    userId: string,
    payload: SetUserDeviceBypassApiRequest
  ): Promise<UserDeviceBypassApiResponse> {
    const response = await this.api.put<ApiResponse<UserDeviceBypassApiResponse>>(
      `/authorized-devices/users/${userId}/bypass`,
      payload
    );
    return this.extractData(response);
  }

  async renameDevice(id: string, payload: RenameAuthorizedDeviceRequest): Promise<AuthorizedDeviceViewModel> {
    const response = await this.api.patch<ApiResponse<AuthorizedDeviceApiDto>>(
      `/authorized-devices/${id}`,
      payload
    );
    return mapAuthorizedDeviceFromApi(this.extractData(response));
  }

  async revokeDevice(id: string): Promise<AuthorizedDeviceViewModel> {
    const response = await this.api.post<ApiResponse<AuthorizedDeviceApiDto>>(
      `/authorized-devices/${id}/revoke`
    );
    return mapAuthorizedDeviceFromApi(this.extractData(response));
  }

  async reactivateDevice(id: string): Promise<AuthorizedDeviceViewModel> {
    const response = await this.api.post<ApiResponse<AuthorizedDeviceApiDto>>(
      `/authorized-devices/${id}/reactivate`
    );
    return mapAuthorizedDeviceFromApi(this.extractData(response));
  }
}

export const authorizedDevicesService = new AuthorizedDevicesService();

export type { AuthorizedDeviceViewModel } from './authorizedDeviceMappers';
